import { Volume2, VolumeX, Music } from "lucide-react";
import { useBackgroundMusic } from "@/context/BackgroundMusicContext";

/**
 * Compact header icon: shows mute/unmute. Also acts as play if paused.
 */
export function BackgroundMusicHeaderToggle() {
  const { available, playing, muted, togglePlay, toggleMute } = useBackgroundMusic();
  if (!available) return null;
  // If paused, clicking plays. If playing, clicking mutes/unmutes.
  const handleClick = () => {
    if (!playing) togglePlay();
    else toggleMute();
  };
  const Icon = !playing || muted ? VolumeX : Volume2;
  const label = !playing ? "Play background music" : (muted ? "Unmute background music" : "Mute background music");
  return (
    <button
      type="button"
      onClick={handleClick}
      data-testid="bg-music-header-toggle"
      title={label}
      aria-label={label}
      className="p-2 text-white/70 hover:text-hotpink transition-colors"
    >
      <Icon size={18} />
    </button>
  );
}

/**
 * Floating pill in the bottom-left. Always-visible play/pause control.
 */
export function BackgroundMusicFloatingPill() {
  const { available, playing, muted, togglePlay, toggleMute } = useBackgroundMusic();
  if (!available) return null;
  return (
    <div
      data-testid="bg-music-floating"
      className="fixed bottom-24 left-3 sm:bottom-28 sm:left-5 z-40 flex items-center gap-1 bg-ink/90 backdrop-blur-md border border-hotpink/40 rounded-full pl-2 pr-1 py-1 shadow-[3px_3px_0px_#FF007F]"
    >
      <Music size={12} className="text-hotpink ml-1" />
      <span className="text-[10px] uppercase tracking-widest text-white/70 font-outfit hidden sm:inline ml-1 mr-1">music</span>
      <button
        type="button"
        onClick={togglePlay}
        data-testid="bg-music-play-toggle"
        aria-label={playing ? "Pause background music" : "Play background music"}
        title={playing ? "Pause background music" : "Play background music"}
        className="w-7 h-7 flex items-center justify-center rounded-full bg-white text-ink hover:bg-hotpink hover:text-white transition-colors text-[10px] font-bold"
      >
        {playing ? <span className="leading-none">‖</span> : <span className="leading-none">▶</span>}
      </button>
      <button
        type="button"
        onClick={toggleMute}
        data-testid="bg-music-mute-toggle"
        aria-label={muted ? "Unmute" : "Mute"}
        title={muted ? "Unmute" : "Mute"}
        className="w-7 h-7 flex items-center justify-center rounded-full text-white/70 hover:text-hotpink transition-colors"
      >
        {muted ? <VolumeX size={14} /> : <Volume2 size={14} />}
      </button>
    </div>
  );
}
